import { useState, useEffect } from "react";
import { ChevronDown, Mail, Send, Globe } from "lucide-react";

const Support = () => {
  const [language, setLanguage] = useState("fr");
  const [openAccordion, setOpenAccordion] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [formStatus, setFormStatus] = useState(null);

  // Détection automatique de la langue au premier chargement
  useEffect(() => {
    const savedLang = localStorage.getItem("nutrinori-support-lang");
    if (savedLang) {
      setLanguage(savedLang);
    } else {
      const browserLang = navigator.language || navigator.userLanguage;
      const detectedLang = browserLang.toLowerCase().startsWith("fr") ? "fr" : "en";
      setLanguage(detectedLang);
      localStorage.setItem("nutrinori-support-lang", detectedLang);
    }
  }, []);

  const toggleLanguage = () => {
    const newLang = language === "fr" ? "en" : "fr";
    setLanguage(newLang);
    localStorage.setItem("nutrinori-support-lang", newLang);
  };

  const toggleAccordion = (index) => {
    setOpenAccordion(openAccordion === index ? null : index);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation simple
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setFormStatus({
        type: "error",
        message: language === "fr" 
          ? "Veuillez remplir tous les champs." 
          : "Please fill in all fields."
      });
      return;
    }

    // Création du mailto
    const subject = encodeURIComponent(formData.subject);
    const body = encodeURIComponent(
      `Nom: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`
    );
    window.location.href = `mailto:support@nutrinori.com?subject=${subject}&body=${body}`;

    // Message de succès
    setFormStatus({
      type: "success",
      message: language === "fr"
        ? "Votre client email va s'ouvrir. Merci !"
        : "Your email client will open. Thank you!"
    });

    // Réinitialiser le formulaire
    setTimeout(() => {
      setFormData({ name: "", email: "", subject: "", message: "" });
      setFormStatus(null);
    }, 3000);
  };

  const content = {
    fr: {
      title: "Comment pouvons-nous vous aider ?",
      subtitle: "Retrouvez les réponses aux questions fréquentes ou contactez notre équipe d'assistance.",
      faqTitle: "Questions fréquentes",
      contactTitle: "Nous contacter",
      contactSubtitle: "Notre équipe vous répond généralement sous 48 heures.",
      formName: "Nom",
      formEmail: "Adresse e-mail",
      formSubject: "Sujet",
      formMessage: "Message",
      formSend: "Envoyer",
      footerEditor: "Éditeur",
      footerPrivacy: "Politique de confidentialité",
      footerTerms: "Conditions d'utilisation",
      footerDelete: "Supprimer mon compte",
      copyright: "© 2026 NutriNori. Tous droits réservés.",
      faqs: [
        {
          question: "Compte et connexion",
          answer: "Pour créer un compte NutriNori, téléchargez l'application sur l'App Store ou Google Play et suivez les instructions d'inscription. Si vous rencontrez des problèmes de connexion, vérifiez votre connexion Internet et assurez-vous d'utiliser la dernière version de l'application. Vous pouvez réinitialiser votre mot de passe directement depuis l'écran de connexion en cliquant sur 'Mot de passe oublié'."
        },
        {
          question: "Abonnement NutriNori Pro",
          answer: "L'abonnement NutriNori Pro vous donne accès à des fonctionnalités avancées : analyses nutritionnelles détaillées, historique complet de vos repas, suggestions personnalisées et accès prioritaire aux nouvelles fonctionnalités. Vous pouvez souscrire directement depuis l'application dans la section 'Passer à Pro'. L'abonnement est géré par l'App Store (iOS) ou Google Play (Android)."
        },
        {
          question: "Restaurer un achat",
          answer: "Si vous avez déjà souscrit à NutriNori Pro et que votre abonnement n'apparaît pas, allez dans l'application, ouvrez les Paramètres, puis sélectionnez 'Restaurer les achats'. Vos achats sont liés à votre compte App Store ou Google Play. Assurez-vous d'être connecté avec le même compte que lors de l'achat initial."
        },
        {
          question: "Analyse et scan des repas",
          answer: "NutriNori utilise une technologie d'intelligence artificielle pour analyser vos repas à partir de photos. Pour de meilleurs résultats, prenez des photos claires et bien éclairées de vos plats. L'application identifie automatiquement les aliments et calcule les valeurs nutritionnelles. Vous pouvez corriger ou ajuster les informations détectées en cliquant sur les aliments identifiés."
        },
        {
          question: "Suppression du compte et des données",
          answer: "Vous pouvez supprimer votre compte et toutes vos données à tout moment depuis les Paramètres de l'application, section 'Confidentialité et données', puis 'Supprimer mon compte'. Cette action est irréversible. Toutes vos données personnelles, analyses et historiques seront définitivement supprimés de nos serveurs dans un délai de 30 jours. Vous pouvez également faire une demande via support@nutrinori.com."
        },
        {
          question: "Signaler un problème",
          answer: "Si vous rencontrez un bug ou un problème technique, vous pouvez le signaler directement depuis l'application via Paramètres > Assistance > Signaler un problème. Décrivez le problème en détail et joignez si possible une capture d'écran. Notre équipe technique interviendra dans les plus brefs délais. Pour les problèmes urgents, contactez-nous à support@nutrinori.com."
        }
      ]
    },
    en: {
      title: "How can we help you?",
      subtitle: "Find answers to common questions or contact our support team.",
      faqTitle: "Frequently asked questions",
      contactTitle: "Contact us",
      contactSubtitle: "Our team usually replies within 48 hours.",
      formName: "Name",
      formEmail: "Email address",
      formSubject: "Subject",
      formMessage: "Message",
      formSend: "Send",
      footerEditor: "Publisher",
      footerPrivacy: "Privacy Policy",
      footerTerms: "Terms of Use",
      footerDelete: "Delete my account",
      copyright: "© 2026 NutriNori. All rights reserved.",
      faqs: [
        {
          question: "Account and sign-in",
          answer: "To create a NutriNori account, download the app from the App Store or Google Play and follow the registration instructions. If you experience sign-in issues, check your internet connection and make sure you're using the latest version of the app. You can reset your password directly from the login screen by clicking 'Forgot password'."
        },
        {
          question: "NutriNori Pro subscription",
          answer: "The NutriNori Pro subscription gives you access to advanced features: detailed nutritional analysis, complete meal history, personalized suggestions, and priority access to new features. You can subscribe directly from the app in the 'Upgrade to Pro' section. The subscription is managed by the App Store (iOS) or Google Play (Android)."
        },
        {
          question: "Restore a purchase",
          answer: "If you've already subscribed to NutriNori Pro and your subscription doesn't appear, go to the app, open Settings, then select 'Restore purchases'. Your purchases are linked to your App Store or Google Play account. Make sure you're signed in with the same account you used for the original purchase."
        },
        {
          question: "Meal analysis and scanning",
          answer: "NutriNori uses artificial intelligence technology to analyze your meals from photos. For best results, take clear and well-lit photos of your dishes. The app automatically identifies foods and calculates nutritional values. You can correct or adjust detected information by clicking on identified foods."
        },
        {
          question: "Account and data deletion",
          answer: "You can delete your account and all your data at any time from the app Settings, 'Privacy and data' section, then 'Delete my account'. This action is irreversible. All your personal data, analyses, and history will be permanently deleted from our servers within 30 days. You can also make a request via support@nutrinori.com."
        },
        {
          question: "Report a problem",
          answer: "If you encounter a bug or technical issue, you can report it directly from the app via Settings > Support > Report a problem. Describe the issue in detail and attach a screenshot if possible. Our technical team will intervene as soon as possible. For urgent issues, contact us at support@nutrinori.com."
        }
      ]
    }
  };

  const t = content[language];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#0A0A0A" }}>
      {/* Header avec sélecteur de langue */}
      <div className="border-b" style={{ borderColor: "rgba(255, 255, 255, 0.1)" }}>
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <a href="/" className="text-xl font-semibold" style={{ color: "#3D6B4A" }}>
            NutriNori
          </a>
          <button
            onClick={toggleLanguage}
            className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200"
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.05)",
              color: "#FFFFFF",
              border: "1px solid rgba(255, 255, 255, 0.1)"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.05)";
            }}
            aria-label={language === "fr" ? "Switch to English" : "Passer en français"}
          >
            <Globe className="w-4 h-4" />
            <span className="font-medium">{language === "fr" ? "FR" : "EN"}</span>
            <span style={{ color: "rgba(255, 255, 255, 0.4)" }}>|</span>
            <span style={{ color: "rgba(255, 255, 255, 0.4)" }}>{language === "fr" ? "EN" : "FR"}</span>
          </button>
        </div>
      </div>

      {/* Hero Section */}
      <div className="max-w-4xl mx-auto px-6 py-16 text-center">
        <h1 
          className="text-4xl sm:text-5xl font-bold mb-4"
          style={{ color: "#FFFFFF" }}
        >
          {t.title}
        </h1>
        <p 
          className="text-lg sm:text-xl max-w-2xl mx-auto"
          style={{ color: "rgba(255, 255, 255, 0.7)" }}
        >
          {t.subtitle}
        </p>
      </div>

      {/* FAQ Section */}
      <div className="max-w-3xl mx-auto px-6 pb-16">
        <h2 
          className="text-2xl font-semibold mb-8"
          style={{ color: "#FFFFFF" }}
        >
          {t.faqTitle}
        </h2>

        <div className="space-y-3">
          {t.faqs.map((faq, index) => (
            <div
              key={index}
              className="rounded-xl overflow-hidden transition-all duration-200"
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.03)",
                border: "1px solid rgba(255, 255, 255, 0.08)"
              }}
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left transition-colors duration-200"
                style={{
                  backgroundColor: openAccordion === index ? "rgba(61, 107, 74, 0.1)" : "transparent"
                }}
                aria-expanded={openAccordion === index}
                aria-controls={`faq-content-${index}`}
              >
                <span 
                  className="font-medium text-base pr-4"
                  style={{ color: "#FFFFFF" }}
                >
                  {faq.question}
                </span>
                <ChevronDown
                  className="w-5 h-5 flex-shrink-0 transition-transform duration-200"
                  style={{
                    color: "#3D6B4A",
                    transform: openAccordion === index ? "rotate(180deg)" : "rotate(0deg)"
                  }}
                />
              </button>
              
              <div
                id={`faq-content-${index}`}
                className="overflow-hidden transition-all duration-300"
                style={{
                  maxHeight: openAccordion === index ? "500px" : "0",
                  opacity: openAccordion === index ? 1 : 0
                }}
              >
                <div 
                  className="px-6 pb-5 text-sm leading-relaxed"
                  style={{ color: "rgba(255, 255, 255, 0.7)" }}
                >
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Contact Section */}
      <div 
        className="border-t"
        style={{ borderColor: "rgba(255, 255, 255, 0.1)" }}
      >
        <div className="max-w-3xl mx-auto px-6 py-16">
          <div className="text-center mb-12">
            <h2 
              className="text-2xl font-semibold mb-3"
              style={{ color: "#FFFFFF" }}
            >
              {t.contactTitle}
            </h2>
            <p 
              className="text-base mb-6"
              style={{ color: "rgba(255, 255, 255, 0.7)" }}
            >
              {t.contactSubtitle}
            </p>
            
            <div className="flex items-center justify-center gap-3 mb-8">
              <Mail className="w-5 h-5" style={{ color: "#3D6B4A" }} />
              <a
                href="mailto:support@nutrinori.com"
                className="text-lg font-medium hover:underline"
                style={{ color: "#3D6B4A" }}
              >
                support@nutrinori.com
              </a>
            </div>
          </div>

          {/* Formulaire de contact */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label 
                htmlFor="name"
                className="block text-sm font-medium mb-2"
                style={{ color: "rgba(255, 255, 255, 0.9)" }}
              >
                {t.formName}
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 rounded-lg transition-all duration-200"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  color: "#FFFFFF",
                  outline: "none"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3D6B4A";
                  e.target.style.backgroundColor = "rgba(255, 255, 255, 0.08)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "rgba(255, 255, 255, 0.1)";
                  e.target.style.backgroundColor = "rgba(255, 255, 255, 0.05)";
                }}
              />
            </div>

            <div>
              <label 
                htmlFor="email"
                className="block text-sm font-medium mb-2"
                style={{ color: "rgba(255, 255, 255, 0.9)" }}
              >
                {t.formEmail}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 rounded-lg transition-all duration-200"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  color: "#FFFFFF",
                  outline: "none"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3D6B4A";
                  e.target.style.backgroundColor = "rgba(255, 255, 255, 0.08)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "rgba(255, 255, 255, 0.1)";
                  e.target.style.backgroundColor = "rgba(255, 255, 255, 0.05)";
                }}
              />
            </div>

            <div>
              <label 
                htmlFor="subject"
                className="block text-sm font-medium mb-2"
                style={{ color: "rgba(255, 255, 255, 0.9)" }}
              >
                {t.formSubject}
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 rounded-lg transition-all duration-200"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  color: "#FFFFFF",
                  outline: "none"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3D6B4A";
                  e.target.style.backgroundColor = "rgba(255, 255, 255, 0.08)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "rgba(255, 255, 255, 0.1)";
                  e.target.style.backgroundColor = "rgba(255, 255, 255, 0.05)";
                }}
              />
            </div>

            <div>
              <label 
                htmlFor="message"
                className="block text-sm font-medium mb-2"
                style={{ color: "rgba(255, 255, 255, 0.9)" }}
              >
                {t.formMessage}
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                rows="5"
                className="w-full px-4 py-3 rounded-lg transition-all duration-200 resize-none"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  color: "#FFFFFF",
                  outline: "none"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3D6B4A";
                  e.target.style.backgroundColor = "rgba(255, 255, 255, 0.08)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "rgba(255, 255, 255, 0.1)";
                  e.target.style.backgroundColor = "rgba(255, 255, 255, 0.05)";
                }}
              />
            </div>

            {formStatus && (
              <div
                className="px-4 py-3 rounded-lg text-sm"
                style={{
                  backgroundColor: formStatus.type === "success" 
                    ? "rgba(61, 107, 74, 0.15)" 
                    : "rgba(239, 68, 68, 0.15)",
                  color: formStatus.type === "success" ? "#3D6B4A" : "#EF4444",
                  border: `1px solid ${formStatus.type === "success" ? "rgba(61, 107, 74, 0.3)" : "rgba(239, 68, 68, 0.3)"}`
                }}
                role="alert"
              >
                {formStatus.message}
              </div>
            )}

            <button
              type="submit"
              className="w-full px-6 py-4 rounded-lg font-medium flex items-center justify-center gap-2 transition-all duration-200"
              style={{
                backgroundColor: "#3D6B4A",
                color: "#FFFFFF"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#2A4D34";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#3D6B4A";
              }}
            >
              <Send className="w-4 h-4" />
              {t.formSend}
            </button>
          </form>
        </div>
      </div>

      {/* Footer */}
      <div 
        className="border-t"
        style={{ borderColor: "rgba(255, 255, 255, 0.1)" }}
      >
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div>
              <h3 
                className="text-sm font-semibold mb-3"
                style={{ color: "rgba(255, 255, 255, 0.5)" }}
              >
                {t.footerEditor}
              </h3>
              <p 
                className="text-sm"
                style={{ color: "rgba(255, 255, 255, 0.7)" }}
              >
                {/* Remplacez par les vraies informations de votre entreprise */}
                NutriNori<br />
                {/* Adresse à compléter */}
              </p>
            </div>

            <div>
              <h3 
                className="text-sm font-semibold mb-3"
                style={{ color: "rgba(255, 255, 255, 0.5)" }}
              >
                {language === "fr" ? "Liens utiles" : "Useful links"}
              </h3>
              <div className="space-y-2">
                <a
                  href="/privacy"
                  className="block text-sm hover:underline"
                  style={{ color: "rgba(255, 255, 255, 0.7)" }}
                >
                  {t.footerPrivacy}
                </a>
                <a
                  href="/terms"
                  className="block text-sm hover:underline"
                  style={{ color: "rgba(255, 255, 255, 0.7)" }}
                >
                  {t.footerTerms}
                </a>
                <a
                  href="/delete-account"
                  className="block text-sm hover:underline"
                  style={{ color: "rgba(255, 255, 255, 0.7)" }}
                >
                  {t.footerDelete}
                </a>
              </div>
            </div>
          </div>

          <div 
            className="pt-8 border-t text-center text-sm"
            style={{ 
              borderColor: "rgba(255, 255, 255, 0.1)",
              color: "rgba(255, 255, 255, 0.5)"
            }}
          >
            {t.copyright}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Support;
